import { GridSelectionModel } from '@mui/x-data-grid';
import { KeyedMutator } from 'swr';
import { SERVICES } from '@shared/constants/apiConfig';
import { IDeal, IDealSummary } from '../Deals/interfaces';
import { IAsset, IAssetOption } from '../Assets/interfaces';

export interface IFilesTableProps {
  rows?: IContactFile[];
  reloadFiles: () => void;
  selectedFiles: GridSelectionModel;
  handleSelectedFiles: (files: GridSelectionModel) => void;
  isLoading: boolean;
  dealId?: string;
  assetId?: string;
  assets?: IAsset[];
}

export interface IContactFileResponse {
  contactId: string;
  documents: IContactFile[];
}

export interface IContactFile {
  contactId: string;
  path: string;
  uploadedOn: string;
  stipType: string;
  stipTypeId: string;
  stipTypeNumericId?: number;
  fileName: string;
  uploadedTo: string;
  contactStipId: string;
  isDeleted: boolean;
  uploadedToDocusignDate? : string
  ocrResult? : IOcrDetail;
}

export interface IUploadFilesModalProps {
  dealId: string;
  handleClose: () => void;
  uploadFilesModalPosition: number;
  handleSubmit: (data: IFilesData) => Promise<void>;
  assetId: string;
  assetOptions: IAssetOption[];
}

export interface IUploadFilesFormProps {
  value: IFilesData;
  dealId: string;
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
  assetOptions: IAssetOption[];
  setIsValid: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface IFilesData {
  files: File[];
  docs: IStipFile[];
  assetId?: string;
}

export interface IStipFile {
  dealId?: string;
  stipTypeId: string;
  file?: File;
  contactId?: string;
  assetId?: string;
  borrowerCoBorrowerType?: string;
}

export interface IStip {
  id: string;
  stipTypeId: string;
  stipTypeNumericId: number;
  blobFile: IBlobFile;
  blobDownloadLink:string;
  content: string;
  ocrResult: IOcrDetail;
  width: number;
  height: number;
  assetId: string;
  dealId: string;
  contactId: string;
  vinNumber: string;
  mileage: string;
  firstName: string;
  lastName: string;
  dlFirstName: string;
  dlLastName: string;
  dlMiddleName: string;
  dlDob: string;
  dlState: string;
  dlIssueDate: string;
  dlExpDate: string;
  dlCurStreet: string;
  dlCurUnit: string;
  dlCurState: string;
  dlCurCity: string;
  dlCurZip: string;
  dlAddressMatch: boolean;
  dlContactnameMatch: boolean;
  dlDobMatch: boolean;
  dlStateMatch: boolean;
  dlIssueDateMatch: boolean;
  dlExpiryDateMatch: boolean;
  dlLicesnseMatch: boolean;
  dlNumber: string;
  regIssueState: string;
  regExpDate: string;
  regOwners: string;
  regVin: boolean;
  regNameMatch: boolean;
  insCompany: string;
  insPhone: string;
  insPolicy: string;
  insEffDate: string;
  insExpDate: string;
  insVin: boolean;
  insNameMatchs: boolean;
  bokBookoutDate: string;
  bokVinMatchs: boolean;
  bokMileageMatchs: boolean;
  boklenderValuation: string;
  bokValuationMatch: number;
  bokAmount: number;
  bokKBB: string;
  bokNADA: string;
  bokMSRP: number;
  bokStateMatchs: boolean;
  bokDoubleAdds: boolean;
  odoVin: string;
  odoYearMakModel: string;
  odoMileage: string;
  appAplicationNumber: string;
  appApprovalDate: string;
  appApprovedAmount: number;
  appContractDate: string;
  appLender: string;
  appRate: number;
  appTerm: number;
  appFrontEndAmount: number;
  appTotalAmountFinanced: number;
  appCreditScore: number;
  appLTV: number;
  appTier: string;
  appTierJump: boolean;
  carFaxVinMatch: boolean;
  carFaxMileageMatchs: boolean;
  carFaxClearTitle: boolean;
  coContactId: string;
  lenderId: string;
  lenderCode: string;
  bookOutValuation: string;
  lcReview: boolean;
  lpReview: boolean;
  lcApprovalDate: string;
  lpApprovalDate: string;
  lcNote: string;
  lpNote: string;
  lc: string;
  lp: string;
  stipReviewRecordId: string;
  reviewStatus: string;
  reviewStatusNote: string;
  denyReason: string;
  condition: string;
  uploadedToDocusignDate: string;
  uploadedToDocusignBy: IUserReference;
  cudlDocumentInfo: ICudlDocumentInfo;
}

export interface IStipReviewModalProps {
  open: boolean;
  stipReviewModalPosition: number;
  onClose: () => void;
  contactStipData: IContactFile;
  confidenceScore?: number;
}

export interface IBlobFile {
  name: string;
  path: string;
  type: string;
}

export interface IStipReviewFormProps {
  stipReview: IStip;
}

export interface IStipsReviewProps {
  contactStipData: IContactFile;
  newTab?: boolean;
  confidenceScore?: number;
}

export enum DocumentSubmitType {
  Disable = 1,
  Flagship = 2,
  Meridian = 3,
  Gls = 4,
  Qlo = 5,
  Wtl = 6,
  Cudl = 7,
  Culs = 8,
}

export const flagshipPushUrl = `${SERVICES.LENDER}/flagship/documents/submit`;
export const meridianPushUrl = `${SERVICES.LENDER}/meridian-link/documents/submit`;
export const glsPushUrl = `${SERVICES.LENDER}/gls/documents/submit`;
export const qloPushUrl = `${SERVICES.LENDER}/quilo/documents/submit`;
export const wtlPushUrl = `${SERVICES.LENDER}/westlake/documents/submit`;
export const cudlPushUrl = `${SERVICES.LENDER}/origence/documents/submit`;
export const culsPushUrl = `${SERVICES.LENDER}/culs/documents/submit`;

export const pushToLender: { [key: number]: string } = {
  [DocumentSubmitType.Flagship]: flagshipPushUrl,
  [DocumentSubmitType.Meridian]: meridianPushUrl,
  [DocumentSubmitType.Gls]: glsPushUrl,
  [DocumentSubmitType.Qlo]: qloPushUrl,
  [DocumentSubmitType.Wtl]: wtlPushUrl,
  [DocumentSubmitType.Cudl]: cudlPushUrl,
  [DocumentSubmitType.Culs]: culsPushUrl,
};

export const ALERT_TYPES = {
  DELETE: 'delete',
  RESTORE: 'restore',
  ADMIN_DELETE: 'adminDelete',
  PUSH_STIPS: 'pushStips',
  UPLOAD_STIPS: 'uploadStips',
};

export interface ICustomPdfModalProps {
  selectedFiles: IContactFile[];
  fileName: string;
  handleFileNameChange: (fileName: string) => void;
  handleClose: () => void;
  generateCustomPdf: () => void;
  pdfModalPosition: number;
}

export interface IGenerateCustomPdfRequest {
  contactId: string;
  assetId: string;
  dealId: string;
  fileName: string;
  contactStipIds: GridSelectionModel;
}

export interface IContactFiles {
  deal?: IDeal;
  reloadDeal?: KeyedMutator<IDeal>;
  hideAssetDropDown?: boolean;
  assetIdParam?: string;
  dealIdParam?: string;
  dealSummary: IDealSummary[] | [];
  hideDownladAllBtn?: boolean;
}

export const DRIVER_LICENSE_FRONT_STIPS_TYPE = 'Driver\'s License - Front Side';
export const DRIVER_LICENSE_FRONT_REMOVAL_STIPS_TYPE = 'Driver\'s License -Front(Removal)';
export const DRIVER_LICENSE_BACK_STIPS_TYPE = 'Driver\'s License - Back Side';
export const DRIVER_LICENSE_BACK_REMOVAL_STIPS_TYPE = 'Driver\'s License -Back(Removal)';
export const CO_BORROWER_DRIVER_LICENSE_FRONT_STIPS_TYPE = 'Driver\'s License - Front (Co Borrower)';
export const CO_BORROWER_DRIVER_LICENSE_BACK_STIPS_TYPE = 'Driver\'s License - Back (Co Borrower)';
export const APPROVAL_STIPS_TYPE = 'Approval';
export const BOOKOUT_STIPS_TYPE = 'Bookout';
export const INSURANCE_STIPS_TYPE = 'Insurance';
export const REGISTRATION_STIPS_TYPE = 'Registration';
export const REFERENCES_STIPS_TYPE = 'References';
export const PROOF_OF_INCOME_STIPS_TYPE = 'Proof of Income';
export const PROOF_OF_INCOME_SECONDARY_STIPS_TYPE = 'Proof of Income - Secondary';
export const PROOF_OF_INCOME_W2_STIPS_TYPE = 'Proof of Income - W2';
export const PROOF_OF_RESIDENCY_STIPS_TYPE = 'Proof of Residence';
export const TITLE_STIPS_TYPE = 'Title';
export const ODOMETER_STIPS_TYPE = 'Odometer Image';

export const AUTO_SELECT_STIPS_TYPES: readonly string[] = [
  BOOKOUT_STIPS_TYPE,
  INSURANCE_STIPS_TYPE,
  REGISTRATION_STIPS_TYPE,
  REFERENCES_STIPS_TYPE,
  DRIVER_LICENSE_FRONT_STIPS_TYPE,
  DRIVER_LICENSE_BACK_STIPS_TYPE,
  CO_BORROWER_DRIVER_LICENSE_FRONT_STIPS_TYPE,
  CO_BORROWER_DRIVER_LICENSE_BACK_STIPS_TYPE,
  PROOF_OF_INCOME_STIPS_TYPE,
  PROOF_OF_INCOME_SECONDARY_STIPS_TYPE,
  PROOF_OF_INCOME_W2_STIPS_TYPE,
  PROOF_OF_RESIDENCY_STIPS_TYPE,
  TITLE_STIPS_TYPE
];

export interface IOcrDetail {
  confidenceScore: number;
  validationMessages: string[];
  reviewStatus: number;
  reviewStatusNote: string;
  driverLicenseBack: IDriverLicenseBack;
  driverLicenseFront: IDriverLicenseFront;
  insurance: IInsurance;
  odometer: IOdometer;
  proofOfIncome: IProofOfIncome;
  proofOfResidency: IProofOfResidency;
  references: IReferences;
  registration: IRegistration;
  title: ITitle;
  rawData: IRawData;
}
export interface IAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface IRawData {
  fields: string[];
  text: string;
  fileName: string;
}
export interface IDocBase {
  documentType: string;
  confidenceScore: number;
  rawData: IRawData;
  contactId: string;
  assetId: string;
}
export interface IDriverLicenseFront extends IDocBase {
  licenseNumber: string;
  fullName: string;
  dob: string;
  address: IAddress;
  expirationDate: string;
  issueDate: string;
  issuingState: string;
  sex: string;
  height: string;
  weight: string;
  eyeColor: string;
  class: string;
  restriction: string;
  endorsement: string;
}

export interface IDriverLicenseBack extends IDocBase {
  issuingState: string;
  docNumber: string;
  endorsements: string;
  organDonor: string;
  addressChangeNotice: string;
  dmV_URL: string;
  version: string;
}

export interface IInsurance extends IDocBase {
  policyNumber: string;
  effectiveDate: string;
  expirationDate: string;
  vin: string;
  make: string;
  model: string;
  year: string;
  ownerName: string;
  address: IAddress;
  issuerContact: string;
  naicNumber: string;
  insurerName: string;
}

export interface IOdometer extends IDocBase {
  unit: string;
  tripReading: string;
  odometerReading: string;
}

export interface IProofOfIncome extends IDocBase {
  employeeName: string;
  employeeAddress: IAddress;
  employerName: string;
  employerAddress: IAddress;
  employerIDNumber: string;
  wagesTipsOtherCompensation: string;
  federalIncomeTaxWithheld: string;
  socialSecurityWages: string;
  socialSecurityTaxWithheld: string;
  medicareWages: string;
  medicareTaxWithheld: string;
  stateWages: string;
  stateTaxWithheld: string;
  localWages: string;
  localTaxWithheld: string;
  taxYear: string;
  formType: string;
}

export interface IProofOfResidency extends IDocBase {
  fullName: string;
  address: IAddress;
  country: string;
  documentID: string;
  issuer: string;
  issuerType: string;
  accountNumber: string;
  serviceType: string;
  issueDate: string;
  statementDate: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  amountDue: string;
  paymentDueDate: string;
  contactNumber: string;
  email: string;
  barcodeData: string;
  referenceNumber: string;
}

export interface IReferenceItem {
  name: string;
  contactNumber: string;
  relation: string;
  address: IAddress;
  email: string;
}

export interface IReferences extends IDocBase {
  references: IReferenceItem[];
}

export interface IRegistration extends IDocBase {
  plateNumber: string;
  vin: string;
  year: string;
  make: string;
  model: string;
  style: string;
  fuelType: string;
  titleNumber: string;
  grossWeight: string;
  classification: string;
  customerID: string;
  ownerName: string;
  address: IAddress;
  county: string;
  registrationNumber: string;
  issueDate: string;
  expiryDate: string;
  inspectionDueDate: string;
  amountPaid: string;
  dlNumber: string;
}

export interface ITitle extends IDocBase {
  vin: string;
  ownerName: string;
  address: IAddress;
  issueDate: string;
  lienholder: string;
  odometerReading: string;
}

export interface IUserReference {
  id: string;
  email: string;
  name: string;
}

export interface ICudlDocumentInfo {
  'Application ID': string;
  Status: string;
  Rate: string;
  Term: string;
  'External Ref No': string;
  'Approval Date': string;
  'Decline Date': string;
  'Counter Date': string;
  Vin: string;
  Amount: string;
}